import { Alert, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue } from '@/shared/lib/money'
import type { StockAction } from './StockMovementModal'
import { stockResultKind, type StockOperation, type StockResultKind } from './stockApi'

interface StockOperationAlertProps {
  operation: StockOperation
  action: StockAction
  onClose: () => void
}

/**
 * Ombor amalining natijasi — MUVAFFAQIYAT, lekin har doim ham "shunchaki
 * bo'ldi" emas.
 *
 * Uch narsani ajratib ko'rsatadi va uchalasi ham yashirishga oson:
 *
 * <h2>1. `replayed` — takroriy so'rov edi</h2>
 * `QARORLAR.md` §20.9-D. Server yangi harakat YOZMADI, avvalgi natijani
 * qaytardi. Buni yashirish "muvaffaqiyatli" deb YOLG'ON aytish bo'lardi:
 * operator ikkinchi marta bosganini bilmasdan, qoldiq ikki marta
 * o'zgardi deb o'ylardi.
 *
 * <h2>2. `lowStock` — minimal qoldiqdan pastga tushdi</h2>
 * ⚠️ XATO EMAS, OGOHLANTIRISH. Chegara chiqimni TO'XTATMAYDI (operator
 * mijoz oldida turgan bo'lishi mumkin), shuning uchun server HTTP 200
 * va javob ichida belgi qaytaradi (`StockOperation` javadoc'i,
 * `CLAUDE.md` §3.2 4-qoidasi bilan bir xil mantiq).
 *
 * <h2>3. Ko'chirish IKKI qator yozadi</h2>
 * `TRANSFER_OUT` + `TRANSFER_IN`. Bitta qator ko'rsatish "ko'chirildi,
 * lekin qayerga?" degan holatni yaratardi.
 *
 * ⚠️ Nol farqli inventarizatsiya ham shu yerga tushadi: server hech
 * qanday harakat yozmaydi (`movements` bo'sh) va buni AYTISH kerak,
 * aks holda operator "bosdim, jurnalda yo'q" deb hisoblardi.
 *
 * <h2>4. `approval` — amal BAJARILMADI, ikkinchi imzo kutilmoqda</h2>
 * ⚠️ ENG QIMMAT HOLAT VA UNI YASHIRISH ENG OSON. Chegaradan oshgan
 * kontragentsiz hisobdan chiqarishda server `movements` ni BO'SH
 * qaytaradi va so'rovni `PENDING` holatda yozadi (`StockOperation`
 * javadoc'i). Bu tekshiruv qo'shilmasidan oldingi shart (`empty`)
 * uni "hech narsa o'zgarmadi" degan xabarga tushirardi — ya'ni
 * operator amalim bekor bo'ldi deb, uni QAYTA yozardi va ikkinchi
 * so'rov paydo bo'lardi (§20.9-D dagi aynan o'sha xavf).
 *
 * ⚠️ HTTP 200 va bu to'g'ri: "so'rov noto'g'ri" emas, "qabul qilindi,
 * lekin hali kuchga kirmadi" (`CLAUDE.md` §8 dagi `PARTIAL`). Rang
 * SARIQ — hech qachon "bajarildi" deb yolg'on aytilmaydi.
 */
export function StockOperationAlert({ operation, action, onClose }: StockOperationAlertProps) {
  const { t } = useTranslation()

  const low = operation.lowStock
  const empty = operation.movements.length === 0
  const approval = operation.approval

  /**
   * ⚠️ XABAR TANLOVI SOF FUNKSIYADA (`stockResultKind`), shu yerda
   * emas. Ilgari u uch qavatli uchlik operator edi va uning TARTIBI
   * ma'noni belgilardi — bunday nuqson sinovda ko'rinmasdi.
   */
  const kind = stockResultKind(operation)
  const awaiting = kind === 'AWAITING' || kind === 'AWAITING_REPLAY'

  const type = low !== null || empty ? 'warning' : 'success'

  const MESSAGE: Record<StockResultKind, string> = {
    // ⚠️ `replayed` kutish holatida ham YASHIRILMAYDI, lekin xabari
    // boshqacha: "shu kalit bo'yicha so'rov ALLAQACHON kutmoqda",
    // ya'ni ikkinchi so'rov YOZILMADI (§20.9-D).
    AWAITING_REPLAY: t('stock.result.approvalReplayed'),
    AWAITING: t('stock.result.approvalPending'),
    REPLAYED: t('stock.result.replayed'),
    NO_CHANGE: t('stock.result.noChange'),
    DONE: t(`stock.action.${action}.done`, { count: operation.movements.length }),
  }

  return (
    <Alert
      type={type}
      showIcon
      closable
      onClose={onClose}
      style={{ marginBottom: 16 }}
      message={MESSAGE[kind]}
      description={
        <Space direction="vertical" size={2}>
          {awaiting && approval !== null ? (
            <>
              <Typography.Text type="warning">
                {t('stock.result.approvalThreshold', {
                  cost: formatMoneyValue(approval.estimatedCost),
                  threshold: formatMoneyValue(approval.threshold),
                })}
              </Typography.Text>
              <Typography.Text type="secondary">
                {t('stock.result.approvalWhere')}
              </Typography.Text>
            </>
          ) : null}
          {operation.movements.map((movement) => (
            <Typography.Text key={movement.id} type="secondary">
              {t('stock.result.line', {
                kind: t(`movementKind.${movement.kind}`),
                quantity: movement.quantity > 0 ? `+${String(movement.quantity)}` : String(movement.quantity),
              })}
            </Typography.Text>
          ))}
          {low === null ? null : (
            <Typography.Text type="warning">
              {t('stock.result.lowStock', {
                sku: low.sku,
                quantity: low.quantity,
                min: low.minQuantity,
              })}
            </Typography.Text>
          )}
        </Space>
      }
    />
  )
}
